import { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { useRouter } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResponderNames } from '@atlassian/eoc-responder-names';
import {
	filterNoOne,
	NavigationMode,
	type RequestedResponder,
	saveTextAsFile,
} from '@atlassian/eoc-schedule-common';

import { messages as commonMessages } from '../../common/messages';
import { completeTaskEndpoint } from '../../services/endpoints/misc';
import { endpoints as scheduleEndpoints } from '../../services/endpoints/schedule';
import { getTeamIdFromContext } from '../../utils';
import { useStore as useScheduleStore } from '../schedule-store';

import { messages } from './messages';

const useExportTimelineService = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const defaultCatch = useDefaultCatch();

	const [isLoading, setIsLoading] = useState(false);

	const exportTimeline = useCallback(async () => {
		try {
			setIsLoading(true);

			const { filename, content } = await scheduleEndpoints.exportTimeline({
				scheduleId,
			});

			// TODO: find proper fallback
			saveTextAsFile(content, filename ?? 'file.ics');

			flagApi.showFlag({
				...successToastProps(),
				title: intlApi.formatMessage(messages.exportTimelineSuccessTitle),
				isAutoDismiss: true,
			});
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'useExportTimelineService-exportTimeline',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.exportTimelineErrorTitle),
								actions: [
									{
										content: intlApi.formatMessage(commonMessages.tryAgain),
										onClick: () => exportTimeline(),
									},
								],
							});
						}
					},
				},
				error,
			);
		} finally {
			setIsLoading(false);
		}
	}, [defaultCatch, flagApi, intlApi, scheduleId]);

	return { isLoading, exportTimeline };
};

const useNamesLoading = () => {
	const [{ selectedView, timelineData, calendarData }] = useScheduleStore();
	const responders = useMemo(() => {
		let uniqueResponders: RequestedResponder[] = [];

		if (selectedView === NavigationMode.Timeline) {
			const layers = [
				...(timelineData?.defaultLayers ? timelineData?.defaultLayers : []),
				...(timelineData?.overrideLayers ? timelineData?.overrideLayers : []),
				...(timelineData?.finalLayers ? timelineData?.finalLayers : []),
			];
			uniqueResponders = layers.reduce<RequestedResponder[]>((acc, layer) => {
				layer.periods.forEach((period) => {
					const { id, type } = period.responder;
					if (!acc.some((responder) => responder.id === id)) {
						acc.push({ id, type });
					}
				});
				return acc;
			}, []);
		}

		if (selectedView === NavigationMode.Calendar) {
			uniqueResponders = calendarData
				? calendarData.finalWeeks.reduce<RequestedResponder[]>((acc, week) => {
						week.rotations.forEach((rotation) => {
							rotation.periods.forEach((period) => {
								const { id, type } = period.responder;
								if (!acc.some((responder) => responder.id === id)) {
									acc.push({ id, type });
								}
							});
						});
						return acc;
					}, [])
				: [];
		}

		return uniqueResponders;
	}, [
		calendarData,
		selectedView,
		timelineData?.defaultLayers,
		timelineData?.finalLayers,
		timelineData?.overrideLayers,
	]);
	const users = useResponderNames(filterNoOne(responders));

	const isLoading = useMemo(() => users.some((user) => user.isLoading), [users]);

	return { isLoading };
};

const useCompleteTask = () => {
	const [routerContext] = useRouter();
	const teamId = getTeamIdFromContext(routerContext);

	const completeTask = useCallback(() => {
		return completeTaskEndpoint(teamId);
	}, [teamId]);

	return { completeTask };
};

export { useExportTimelineService, useNamesLoading, useCompleteTask };
